import React, { useMemo, useState } from 'react';

import { action } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { update } from 'serializr';

import capitalize from 'lodash/capitalize';

import theme from '@vromo/ui/components/theme';
import Title from '@vromo/ui/components/Title';
import Alert from '@vromo/ui/components/Alert';
import Select from '@vromo/ui/components/Select2';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { DropDown, DropDownChoice } from '@vromo/ui/components/DropDown';
import Checkbox from '@vromo/ui/components/Checkbox';

import { conditionLabels, PolicyRule, stackingLabels } from '@vromo/models';

import styled from '@emotion/styled';

import { Condition, conditionDefinitions } from './Condition';
import ConditionEditor from './ConditionEditor';
import sortBy from 'lodash/sortBy';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { RootStore } from '../stores/RootStore';
import Flex from '@vromo/ui/components/Flex';
import { ActionButton, ClearError } from './components/styled';
import { useTranslation } from 'react-i18next';
import { qaLabelAutodispatcher } from '../../../../common-qa-labels/autodispatcher';
import { buildDynamicLabel } from '../../../../common-qa-labels/utils';

const RULE_MODES = ['offer', 'dispatch'];

const ModePicker = styled.div`
    align-items: center;
    background-color: ${theme.lightGrey};
    border-radius: 3px;
    border: 1px solid ${theme.lightGrey};
    display: flex;
    font-weight: bold;
    margin-right: 15px;
    padding: 10px;
`;

// const ruleProperties = Array.from(conditionDefinitions.keys()).map(value => ({ value, label: conditionDefinitions.get(value)!.label }))

const ruleProperties = [
    ...conditionLabels,
    ...stackingLabels
].map(item => ({ value: item, label: conditionDefinitions.get(item)!.label }));

const RuleEditor = observer<{
    addRule: (r: PolicyRule) => void;
    cancel: () => void;
    defaultRule: Partial<PolicyRule>;
    rule: PolicyRule;
    teamOptions: Array<{ label: string | null; value: number | boolean | null; }>;
}>(function RuleEditor({
    addRule,
    cancel,
    defaultRule,
    rule,
    teamOptions,
}) {
    const { t } = useTranslation();
    const [showAlert, setShowAlert] = useState(false);
    const [currentTeam, setCurrentTeam] = useState(
        rule.team != null ? rule.team : defaultRule.team
    );
    const [currentMode, setCurrentMode] = useState(rule.mode);
    const [includeSubgroups, setIncludeSubgroups] = useState(!!rule.includeSubgroups);
    const {vehicleStore} = useStore<RootStore>('rootStore');
    
    const conditionList = useLocalObservable(() => constructConditionList(rule));
    
    const availableTeams = useMemo(() => sortBy(
        teamOptions, (t) => t.label
    ), [currentMode, teamOptions, vehicleStore]);
            
        
    function constructConditionList(ruleModel: PolicyRule) {
        const conditions = [
            ...ruleModel.fieldDescriptions,
            ...ruleModel.stacking?.fieldDescriptions || []
        ].map((k) => new Condition({ property: k.condition, value: k.value }));
        
        if (!conditions.length) conditions.push(new Condition());

        return conditions;
    }

    const isValidUpdate = (update: Condition[]) => {
        return update
            .every(({ property, value }) => {
                if (!property) return false;
                const def = conditionDefinitions.get(property!);
                if (def && def.type === 'enum') return !!value;
                if (!def?.max) return true;
                const numberValue = Number(value) as number;
                return numberValue > 0 && numberValue < def.max;
            })
    };

    const handleIncludeSubgroupsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIncludeSubgroups(!!e.target.checked)
    }

    const saveRule = action(() => {
        if (!isValidUpdate(conditionList)) {
            setShowAlert(true);
            return
        };
        const ruleUpdate = {
            team: currentTeam!,
            mode: currentMode,
            includeSubgroups,
            ...conditionList.reduce((conditionUpdate, { property, value, unit, type }) => {
                let newVal = value;
                if (unit) newVal = Number(newVal) * unit.multiplier;
                switch (type) {
                    case 'enum':
                        break;
                    case 'boolean':
                        newVal = true;
                        break;
                }
                if (conditionLabels.includes(property!)) {
                    return {
                        ...conditionUpdate,
                        [property!]: newVal
                    }
                } else {
                    return {
                        ...conditionUpdate,
                        stacking: {
                            ...conditionUpdate.stacking,
                            [property!]: newVal,
                        }
                        
                    }
                }
            }, { stacking: {}}),
        };
        update(rule, { ...defaultRule, ...ruleUpdate });
        addRule(rule);
    });

    return (
        <div>
            <Title data-testid={qaLabelAutodispatcher.ruleModal.title} style={{ margin: 0 }}>{t('ruleEditor.newRule')}</Title>
            {showAlert ? (
                <Alert danger>
                    <Flex horizontal="between">
                        {t('ruleEditor.error')}
                        <ClearError
                            icon="times"
                            onClick={() => {
                                setShowAlert(false);
                            }}
                        />
                    </Flex>
                </Alert>
            ) : null}

            <Flex vertical='middle' style={{margin: '10px 0px'}} horizontal='left'>
                <DropDown data-testid={qaLabelAutodispatcher.ruleModal.dispatchChoiceDropdown}
                    button={
                        <ModePicker>
                            <h3 style={{ margin: 0 }}>{capitalize(currentMode || '')}</h3>
                            <FontAwesome icon="caret-down" style={{ marginLeft: 10 }} />
                        </ModePicker>
                    }>
                    {RULE_MODES.map((rm) => (
                        <DropDownChoice data-testid={buildDynamicLabel(qaLabelAutodispatcher.ruleModal.dispatchChoiceDropdown,rm)}
                            value={rm}
                            current={currentMode!}
                            label={capitalize(rm)}
                            onChange={() => setCurrentMode(rm)}
                            key={`${rm}`}
                        />
                    ))}
                </DropDown>
                <h3>{t('ruleEditor.group')}</h3>
            </Flex>
            <div data-testid={qaLabelAutodispatcher.ruleModal.teamsDropdown}>
                <Select 
                    value={currentTeam ? teamOptions.find((t) => t.value === currentTeam) : null}
                    onChange={(selected: { label: string | null; value: number | null }) => setCurrentTeam(selected.value!)}
                    options={availableTeams}
                />
            </div>
            <Checkbox
                data-testid={qaLabelAutodispatcher.ruleModal.includeSubteamsCheckbox}
                checked={includeSubgroups}
                onChange={handleIncludeSubgroupsChange}
                label={t('ruleEditor.subTeams')}
            />
            <h3>{t('ruleEditor.when')}</h3>
            <ConditionEditor
                conditionList={conditionList}
                ruleProperties={ruleProperties}
            />
            <Flex horizontal="right">
                <ActionButton
                    onClick={ cancel}>
                    {t('ruleEditor.cancel')}
                </ActionButton>
                <ActionButton data-testid={qaLabelAutodispatcher.ruleModal.saveRuleBtn}
                    onClick={saveRule}
                    primary>
                    {t('ruleEditor.save')}
                </ActionButton>
            </Flex>
        </div>
    );
});

export default RuleEditor;
